import { FC } from 'react';
import { Alert, Button } from 'react-daisyui';
import { Link, useNavigate } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import Stepper from '@/components/shared/Stepper/Stepper.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import * as z from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useTranslation } from 'react-i18next';
import { isValidPhoneNumber } from 'libphonenumber-js';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import SelectInput from '@/components/functional/SelectInput/SelectInput.tsx';
import useRegisterUser from '@/api/useRegisterUser.ts';
import CheckboxInput from '@/components/functional/CheckboxInput/CheckboxInput.tsx';
import { passToRouteSearchParams } from '@/utils/passToRouteSearchParams.ts';
import { passwordSchema } from '$/utils/validators/passwordSchema.ts';
import { trackEvent } from '@/utils/analytics.ts';
import ContinueWithGoogleButton from '@/components/functional/Oauth/ContinueWithGoogleButton/ContinueWithGoogleButton.tsx';
import ContinueWithFacebookButton from '@/components/functional/Oauth/ContinueWithFacebookButton/ContinueWithFacebookButton.tsx';
import ContinueWithAppleButton from '@/components/functional/Oauth/ContinueWithAppleButton/ContinueWithAppleButton.tsx';
import CheckboxGroup from '@/components/functional/CheckboxInput/CheckboxGroup.tsx';
import { availableRoles, genderOptions } from '$/requests/RegisterUser.ts';
import { isValidUkPostcode, normaliseUkPostcode } from '@/utils/postcodeDistance.ts';

const genderSelectOptions: { value: string; label: string }[] = genderOptions.map((value) => ({
  value,
  label:
    value === 'prefer_not_to_say' ? 'Prefer not to say' : value.charAt(0).toUpperCase() + value.slice(1),
}));

type Schema = RoleStepSchema & SignUpStepSchema;

const steps = ['select-role', 'sign-up', 'success-sign-up'] as const;
const roleOptions = [
  { value: availableRoles[0], labelKey: 'registrationPage.roleBusinessProvider' },
  { value: availableRoles[1], labelKey: 'registrationPage.roleFamilyMember' },
] as const;
const RegisterPage: FC = () => {
  return (
    <Stepper.Wrapper steps={steps} initialStep="select-role">
      <Stepper.Step<typeof steps> step="select-role">
        <StepSelectRole />
      </Stepper.Step>
      <Stepper.Step<typeof steps> step="sign-up">
        <StepSignUp />
      </Stepper.Step>
      <Stepper.Step<typeof steps> step="success-sign-up">
        <StepSuccessSignUp />
      </Stepper.Step>
    </Stepper.Wrapper>
  );
};

export default RegisterPage;

const roleStepSchema = z.object({
  roles: z.array(z.enum(availableRoles)).min(1, { message: 'Select at least one role' }),
});

type RoleStepSchema = z.infer<typeof roleStepSchema>;

const signUpStepSchema = z
  .object({
    first_name: z.string().min(1, { message: 'This field is required.' }),
    last_name: z.string().min(1, { message: 'This field is required.' }),
    gender: z.string().optional(),
    phone_number: z
      .string()
      .min(1, { message: 'This field is required.' })
      .refine((value) => isValidPhoneNumber(value), {
        message: 'Please specify a valid phone number (include the international prefix).',
      }),
    city_town: z.string().optional(),
    postcode: z
      .string()
      .min(1, { message: 'This field is required.' })
      .transform((value) => normaliseUkPostcode(value))
      .refine((value) => isValidUkPostcode(value), { message: 'Enter a valid UK postcode' }),
    email: z.string().min(1, { message: 'This field is required.' }).email('Invalid email'),
    password: passwordSchema,
    repeat_password: z.string().min(1, { message: 'This field is required.' }),
    terms: z.boolean().refine((value) => value, { message: 'You must agree to the terms and conditions' }),
  })
  .superRefine(({ repeat_password, password }, ctx) => {
    if (repeat_password !== password) {
      ctx.addIssue({
        code: 'custom',
        message: 'The passwords did not match',
        path: ['repeat_password'],
      });
    }
  });

type SignUpStepSchema = z.infer<typeof signUpStepSchema>;
function StepSelectRole() {
  const { t } = useTranslation();
  const { goToStep, setContextData, contextData } = useStepperContext<typeof steps, Schema>();
  const { control, handleSubmit } = useForm<RoleStepSchema>({
    resolver: zodResolver(roleStepSchema),
    defaultValues: {
      roles: contextData?.roles ?? [],
    },
  });

  const handleNext = (data: RoleStepSchema) => {
    setContextData((prev) => ({ ...prev, roles: data.roles }));
    goToStep('sign-up');
  };

  return (
    <BrandCardSideContent wide>
      <form className="w-[428px] max-w-full flex flex-col gap-4" onSubmit={handleSubmit(handleNext)}>
        <div className="flex flex-col gap-4">
          <h1 className="text-3xl font-semibold">{t('registrationPage.chooseRoleTitle')}</h1>
          <p className="text-text-secondary">{t('registrationPage.chooseRoleSubtitle')}</p>
          <Controller
            control={control}
            name="roles"
            render={({ field: { value, onChange }, formState: { errors } }) => (
              <div className="flex flex-col gap-2">
                <CheckboxGroup.Group value={value} onChange={onChange} error={errors.roles?.message} color="primary">
                  <div className="flex flex-col gap-2">
                    {roleOptions.map((role) => (
                      <CheckboxGroup.Input key={role.value} value={role.value} label={t(role.labelKey)} />
                    ))}
                  </div>
                </CheckboxGroup.Group>
                {errors.roles?.message && <span className="text-sm text-error">{errors.roles.message}</span>}
              </div>
            )}
          />
          <LoadingButton type="submit" color="primary" loading={false}>
            {t('button.next')}
          </LoadingButton>
        </div>
      </form>
    </BrandCardSideContent>
  );
}
function StepSignUp() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    mutateAsync: registerUser,
    isError: isErrorRegisterUser,
    isPending: isLoadingRegisterUser,
    error: errorRegisterUser,
  } = useRegisterUser();
  const { goToStep, setContextData, contextData } = useStepperContext<typeof steps, Schema>();
  const { control, handleSubmit } = useForm<SignUpStepSchema>({
    resolver: zodResolver(signUpStepSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      gender: '',
      phone_number: '',
      city_town: '',
      postcode: '',
      email: '',
      password: '',
      repeat_password: '',
      terms: false,
    },
  });

  const handleNextStep = (data: SignUpStepSchema) => {
    void (async () => {
      try {
        if (isLoadingRegisterUser) return;
        const roles = (contextData?.roles as RoleStepSchema['roles']) ?? [];

        if (!roles.length) {
          goToStep('select-role');
          return;
        }

        await registerUser({
          first_name: data.first_name,
          last_name: data.last_name,
          gender: (data.gender || undefined) as (typeof genderOptions)[number] | undefined,
          phone_number: data.phone_number,
          city_town: data.city_town,
          postcode: data.postcode,
          email: data.email,
          password: data.password,
          roles,
        });

        const gaRole = roles.includes('business_provider') ? 'carer' : 'family';
        trackEvent({ name: 'sign_up', params: { user_role: gaRole } });

        setContextData({ ...data, roles });
        navigate(
          passToRouteSearchParams(Routes.CONFIRM_EMAIL, {
            email: encodeURIComponent(data.email),
            role: roles.includes('business_provider') ? 'business_provider' : 'family_member',
          })
        );
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent wide>
      <form className="w-[560px] max-w-full flex flex-col gap-4" onSubmit={handleSubmit(handleNextStep)}>
        <div className="flex flex-col gap-4">
          {isErrorRegisterUser && <Alert status="error">{errorRegisterUser.message}</Alert>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Controller
              control={control}
              name="first_name"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextInput
                  error={errors.first_name?.message}
                  value={value}
                  onChange={onChange}
                  label={t('label.firstName')}
                  required
                  placeholder={t('placeholder.firstName')}
                />
              )}
            />
            <Controller
              control={control}
              name="last_name"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextInput
                  error={errors.last_name?.message}
                  value={value}
                  onChange={onChange}
                  label={t('label.lastName')}
                  required
                  placeholder={t('placeholder.lastName')}
                />
              )}
            />
          </div>
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.email?.message}
                value={value}
                onChange={onChange}
                label={t('label.email')}
                required
                placeholder={t('placeholder.email')}
              />
            )}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Controller
              control={control}
              name="password"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextInput
                  error={errors.password?.message}
                  value={value}
                  onChange={onChange}
                  type="password"
                  autoComplete="password"
                  label={t('label.password')}
                  required
                  placeholder={t('placeholder.password')}
                />
              )}
            />
            <Controller
              control={control}
              name="repeat_password"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextInput
                  error={errors.repeat_password?.message}
                  value={value}
                  onChange={onChange}
                  type="password"
                  autoComplete="repeat_password"
                  label={t('label.repeat_password')}
                  required
                  placeholder={t('placeholder.repeat_password')}
                />
              )}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Controller
              control={control}
              name="gender"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <SelectInput
                  error={errors.gender?.message}
                  value={value}
                  onChange={onChange}
                  options={genderSelectOptions}
                  label={t('label.gender')}
                  placeholder={t('placeholder.gender')}
                />
              )}
            />
            <Controller
              control={control}
              name="phone_number"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextInput
                  error={errors.phone_number?.message}
                  value={value}
                  onChange={onChange}
                  type="tel"
                  autoComplete="tel"
                  label={t('label.phoneNumber')}
                  required
                  placeholder={t('placeholder.phoneNumber')}
                />
              )}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Controller
              control={control}
              name="city_town"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextInput
                  error={errors.city_town?.message}
                  value={value}
                  onChange={onChange}
                  label={t('label.cityTown')}
                  placeholder={t('placeholder.cityTown')}
                />
              )}
            />
            <Controller
              control={control}
              name="postcode"
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <TextInput
                  error={errors.postcode?.message}
                  value={value}
                  onChange={(e) => onChange(e.target.value.toUpperCase())}
                  label={t('label.postcode')}
                  required
                  placeholder={t('placeholder.postcode')}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                />
              )}
            />
          </div>
          <Controller
            control={control}
            name="terms"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <CheckboxInput
                onChange={onChange}
                checked={value}
                error={errors.terms?.message}
                label={
                  <Trans i18nKey="registrationPage.agreeToTerms">
                    I accept the
                    <Link target="_blank" className="text-link underline" to={Routes.TERMS}>
                      Terms and Conditions
                    </Link>
                  </Trans>
                }
              />
            )}
          />
          <LoadingButton loading={isLoadingRegisterUser} type="submit" color="primary">
            {t('button.createAccount')}
          </LoadingButton>
          {import.meta.env.VITE_GOOGLE_LOGIN_ENABLED === 'true' && (
            <ContinueWithGoogleButton copy={t('button.registerWithGoogle')} />
          )}
          {import.meta.env.VITE_FACEBOOK_LOGIN_ENABLED === 'true' && (
            <ContinueWithFacebookButton copy={t('button.registerWithFacebook')} />
          )}
          {import.meta.env.VITE_APPLE_LOGIN_ENABLED === 'true' && (
            <ContinueWithAppleButton copy={t('button.registerWithApple')} />
          )}
        </div>
        <div className="flex justify-end">
          <Link to={Routes.LOGIN}>
            <Button className="bg-white text-black hover:opacity-80">{t('button.loginToExistingAccount')}</Button>
          </Link>
        </div>
      </form>
    </BrandCardSideContent>
  );
}

function StepSuccessSignUp() {
  const { t } = useTranslation();
  const { contextData } = useStepperContext<typeof steps, Schema>();

  return (
    <BrandCardSideContent wide>
      <div className="flex flex-col justify-center items-center gap-8 w-[343px] max-w-full">
        <div className="flex flex-col gap-4 items-center text-center">
          <h2 className="font-medium text-5xl">{t('registrationPage.congrats')}</h2>
          <p className="text-text-secondary">{t('registrationPage.congratsText')}</p>
        </div>
        <Link
          className="w-full flex flex-col"
          to={passToRouteSearchParams(Routes.CONFIRM_EMAIL, {
            email: encodeURIComponent(contextData?.email as string),
          })}
        >
          <Button color="primary">{t('button.gotIt')}</Button>
        </Link>
      </div>
    </BrandCardSideContent>
  );
}
